import React, { useRef, useState, useEffect } from 'react';
import CardContainer from './CardContainer.redesign';
import { ITableConfig } from 'src/components/common/table';
import * as _ from 'lodash';
import { ODataOptions, ODataOrderByDirection } from 'src/util/ODataBuilder';

export interface IPagedItemProps {
    className?: string;
    resetLastPage?: boolean;
    ResetStateCallback: () => void;
    optionalCondition?: boolean;
    style?: React.CSSProperties;
    resultsLabelStyle?: React.CSSProperties;
    orderByColumn?: string;
    orderByDirection?: ODataOrderByDirection;
    startingPage: number;
    rowsPerPage?: number;
    totalRows: number;
    dataOptions: ODataOptions;
    DisplayList: () => React.ReactNode;
    GetPagedItemList: (dataOptions: ODataOptions, resetSearch: boolean) => void;
    elementId?: string;
    noContainer?: boolean;
    noContainerNextPageAction?: () => void;
    buttonPaging?: boolean;
}

const PagedItemList: React.FC<IPagedItemProps> = (props) => {
    const [pagingDataOptions, setPagingDataOptions] = useState(() => new ODataOptions());
    const [isOnLastPage, setIsOnLastPage] = useState(false);
    const [listScrollTop, SetListScrollTop] = useState(0);
    const listContainerRef = useRef(null);

    useEffect(() => {
        createDataOptions();
        SetListScrollTop(listContainerRef?.current?.clientHeight);
    }, []);

    useEffect(() => {
        setPagingDataOptions(props.dataOptions);
    }, [props.dataOptions]);

    useEffect(() => {
        setIsOnLastPage(isNaN(props.totalRows) || props.totalRows < props.rowsPerPage);
    }, [props.totalRows, props.rowsPerPage]);

    useEffect(() => {
        const pageSize = pagingDataOptions?.getPageSize?.() ?? props.rowsPerPage ?? 0;
        const pageNumber = pagingDataOptions?.getPageNumber?.() ?? props.startingPage ?? 1;
        setIsOnLastPage(isNaN(props.totalRows) || pageSize === 0 || pageNumber * pageSize >= props.totalRows);
    }, [props.totalRows, props.rowsPerPage, props.startingPage, pagingDataOptions]);

    const createDataOptions = () => {
        const initialOptionsConfig: ITableConfig = { rowCount: props.rowsPerPage, pageNumber: props.startingPage};
        let configOptions = ODataOptions.from(initialOptionsConfig);
        let tempDataOptions = new ODataOptions();
        tempDataOptions.addOrderBy(props.orderByColumn, props.orderByDirection);
        tempDataOptions.addPaging(props.startingPage, props.rowsPerPage);
        tempDataOptions.merge(configOptions);
        setPagingDataOptions(tempDataOptions);
    };

    if (props.resetLastPage && isOnLastPage) {
        setIsOnLastPage(false);
        listContainerRef.current.scrollTo({ behavior: 'instant', top: listScrollTop > 0 ? listScrollTop : 100 });
    }

    const incrementPage = () => {
        if (!isOnLastPage && props.totalRows > pagingDataOptions.getPageSize()) {
            pagingDataOptions.incrementPageNumber();
            pagingDataOptions.setIsNextpage(true);
            props.GetPagedItemList(pagingDataOptions, false);
            setIsOnLastPage(pagingDataOptions.getPageNumber() * pagingDataOptions.getPageSize() >= props.totalRows);
        }
    }; 

    const pagedItemListResult: React.ReactNode[] = [];
    const pagedItemListHeader: React.ReactNode[] = [];
    pagedItemListHeader.push(
        <div style={{ textAlign: 'center', backgroundColor: '#f9f9f9', position: 'sticky', ...props.resultsLabelStyle}}>
            Results {props.totalRows > 0 ? props.startingPage : 0} - {props.totalRows < pagingDataOptions?.getPageSize() * pagingDataOptions?.getPageNumber()
                ? props.totalRows : (pagingDataOptions?.getPageSize() * pagingDataOptions?.getPageNumber() < props.totalRows
                ? pagingDataOptions?.getPageSize() * pagingDataOptions?.getPageNumber() : props.totalRows)} of {props.totalRows}.
        </div>
    );
    
    props.noContainer ? pagedItemListResult.push(props.DisplayList()) :
    pagedItemListResult.push(
        <div key="paged-list-container" id={props.elementId} ref={listContainerRef} className={props.className} style={props.style}>
            <div className="dlc-paged-items">{props.children}</div>
            <CardContainer
                showNextPagePanel={!isOnLastPage && props.optionalCondition}
                nextPageAction={incrementPage}
                buttonPaging={props.buttonPaging}
            >
                {props.DisplayList()}
            </CardContainer>
        </div>
    );

    return (
        <>
            {pagingDataOptions && pagedItemListHeader}
            {pagedItemListResult}
        </>
    );
};

PagedItemList.defaultProps = {
    optionalCondition: true,
    startingPage: 1,
    orderByDirection: ODataOrderByDirection.Descending,
};

PagedItemList.displayName = 'PagedItems';
export default PagedItemList;


// WEBPACK FOOTER //
// ./src/components/common/PagedItemList.tsx